/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import { Form, Field, Formik, FormikHelpers } from 'formik';
import Draggable from 'react-draggable';
import { ToolIcon } from 'src/assets/images';
import { Modal } from 'src/features/common';
import Checkbox from '../checkbox/checkbox';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import { Environment, reloadEnvironmentConfig } from 'src/Environment';
import { IDisabledFeatures, IEnvironment, ISentry } from 'src/types';
import { DEBUG_CONFIG_KEY } from 'src/common';
import './dev-tools.scss';

type Entry = string | boolean | IEnvironment | ISentry;

interface DevToolsForm extends IDisabledFeatures {
  search: string;
}

type DevToolsFormValues = {
  search: string;
} & IDisabledFeatures;

export const DevTools = () => {
  const [openModal, setOpenModal] = useState(false);

  function filterObj(allowedKeys: string[], environment: IEnvironment) {
    return Object.keys(environment)
      .filter((key) => allowedKeys.includes(key))
      .reduce((obj, key) => {
        obj[key] = environment[key];
        return obj;
      }, {});
  }

  function onConfirm(values) {
    sessionStorage.setItem(DEBUG_CONFIG_KEY, JSON.stringify(values));
    reloadEnvironmentConfig();
    setOpenModal(false);
    window.location.reload();
  }

  function initialValues() {
    const debugConfig = sessionStorage.getItem(DEBUG_CONFIG_KEY);
    if (debugConfig) {
      return JSON.parse(debugConfig);
    }
    return {
      search: '',
      ...Environment.current.disabledFeatures,
    };
  }

  function convertToSentenceCase(camelCaseText: string) {
    const result = camelCaseText.replace(/([A-Z])/g, ' $1');
    const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
    return finalResult;
  }

  const getHashFromEnvVar = (envVariable: string): string | undefined =>
    envVariable.split(' ').at(-1);

  const getDateFromEnvVar = (envVariable: string): string => {
    const dateElements: string[] = envVariable.split(' ');
    dateElements.pop();

    return dateElements.join(' ');
  };

  const submit = (values: DevToolsFormValues, { setSubmitting }: FormikHelpers<DevToolsForm>) => {
    setSubmitting(false);
    const filteredValues = Object.entries(values).reduce((acc, [key, value]) => {
      if (key !== 'search') {
        acc[key] = value;
      }
      return acc;
    }, {});
    onConfirm(filteredValues);
  };

  return (
    <>
      <Modal visible={openModal} onClose={() => setOpenModal(false)} size="x-large">
        <div className="custom-box">
          <h1 className="dialog-heading">Developer Tools</h1>
          <div className="dev-tools-content">
            <div className="env-container">
              <section className="dev-tools-head-content">
                <div className="dev-tools-head-block">
                  <h2>{Environment.current.name}</h2>
                  <ul className="env-content">
                    {Object.entries<Entry>(filterObj(['env', 'tealium'], Environment.current)).map(
                      ([key, value], index) => (
                        <li className="env-item" key={index}>
                          <p className="env-item-title">{`${key}:`}</p>
                          <p className="env-item-text">{value.toString()}</p>
                        </li>
                      ),
                    )}
                  </ul>
                </div>

                <div className="dev-tools-head-block">
                  <h2>Git information</h2>
                  <ul className="env-content">
                    <li className="env-item">
                      <p className="env-item-title">Version:</p>
                      <p className="env-item-text coming-soon">Coming soon!</p>
                      <p className="env-item-title">Hash:</p>
                      <p className="env-item-text">
                        {getHashFromEnvVar(process.env.REACT_APP_GIT_SHA || '')}
                      </p>
                      <p className="env-item-title">Date:</p>
                      <p className="env-item-text">
                        {getDateFromEnvVar(process.env.REACT_APP_GIT_SHA || '')}
                      </p>
                    </li>
                  </ul>
                </div>
              </section>

              <h2>Disabled features</h2>
              <Formik<DevToolsForm>
                initialValues={initialValues()}
                validateOnChange={true}
                validateOnBlur={true}
                enableReinitialize={true}
                onSubmit={submit}
              >
                {({ values }) => (
                  <Form className="dev-tools-form">
                    <div className="search-box">
                      <label htmlFor="search">Filter:</label>
                      <Field name="search" />
                    </div>

                    <ul className="disabled-features">
                      {Object.keys(Environment.current.disabledFeatures)
                        .filter((key) =>
                          convertToSentenceCase(key)
                            .toLowerCase()
                            .startsWith(
                              values.search !== undefined ? values.search.toLowerCase() : '',
                            ),
                        )
                        .map((key, index) => (
                          <li className="env-item" key={index}>
                            <p className="env-item-title">{`${convertToSentenceCase(key)}`}</p>
                            <Field
                              name={key}
                              type="checkbox"
                              component={MiniAppFocusWrapper}
                              innerComponent={Checkbox}
                              checkboxText=""
                            />
                          </li>
                        ))}
                    </ul>
                    <div className="modal-button-group">
                      <button
                        type="button"
                        onClick={() => setOpenModal(false)}
                        className="dev-tools-secondary-button"
                      >
                        Cancel
                      </button>
                      <button type="submit" className="dev-tools-primary-button">
                        Submit
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </Modal>
      <Draggable>
        <button type="button" className="dev-tools-button">
          <img
            src={ToolIcon}
            alt="tool"
            onClick={() => setOpenModal((prev) => !prev)}
            onTouchEnd={() => setOpenModal((prev) => !prev)}
          />
        </button>
      </Draggable>
    </>
  );
};
